import { useAtomValue } from 'jotai'
import { memo } from 'react'
import { layerAtomFamily, layerTreeAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Layer, LayerNode } from '../data/v1'
import { BackdropLayerRenderer } from './backdrop'
import { ContentLayerRenderer } from './content'
import { FloatWrapper } from './float-wrapper'
import { ImageLayerRenderer } from './image'
import { ShapeLayerRenderer } from './shape'
import { SubstackLayerRenderer } from './substack'
import { TextLayerRenderer } from './text'

const LayerNodeRenderer = memo(function LayerNodeRenderer({ node }: { node: LayerNode }) {
	const store = useCanvasStore()
	const layer = useAtomValue(layerAtomFamily(node.id), { store })

	if (!layer || layer.hidden) { return null }

	const children = [...node.children].reverse().map(child => <LayerNodeRenderer key={child.id} node={child} />)

	const element = <LayerByType layerId={node.id} layer={layer}>{children}</LayerByType>

	if ('floating' in layer && layer.floating) {
		return (
			<FloatWrapper x={layer.floatX} y={layer.floatY} anchor={layer.floatAnchor}>
				{element}
			</FloatWrapper>
		)
	}

	return element
})

function LayerByType({ layerId, layer, children }: { layerId: string; layer: Layer; children: React.ReactNode }) {
	switch (layer.type) {
		case 'content':
			return (
				<ContentLayerRenderer
					layerId={layerId}
					xPin={layer.xPin}
					yPin={layer.yPin}
					textAlignMode={layer.textAlignMode}
					textAlignValue={layer.textAlignValue}
					spacing={layer.spacing}
					gap={layer.gap}
					textStyle={layer.textStyle}
				>
					{children}
				</ContentLayerRenderer>
			)
		case 'text':
			return (
				<TextLayerRenderer
					layerId={layer.id}
					content={layer.content}
					refTo={layer.refTo}
					customValue={layer.customValue}
					textAlignMode={layer.textAlignMode}
					textAlignValue={layer.textAlignValue}
					spacing={layer.spacing}
					background={layer.background}
					textStyle={layer.textStyle}
					textHighlight={layer.textHighlight}
					borderRadius={layer.borderRadius}
					borderRadiusCorners={layer.borderRadiusCorners}
				/>
			)
		case 'image':
			return (
				<ImageLayerRenderer
					layerId={layerId}
					content={layer.content}
					refTo={layer.refTo}
					customValue={layer.customValue}
					width={layer.width}
					spacing={layer.spacing}
					background={layer.background}
					borderRadius={layer.borderRadius}
					borderRadiusCorners={layer.borderRadiusCorners}
					opacity={layer.opacity}
				/>
			)
		case 'shape':
			return (
				<ShapeLayerRenderer
					layerId={layerId}
					shapeKind={layer.shapeKind}
					preset={layer.preset}
					fillMode={layer.fillMode}
					fill={layer.fill}
					shapeFit={layer.shapeFit}
					width={layer.width}
					height={layer.height}
					background={layer.background}
					borderRadius={layer.borderRadius}
					borderRadiusCorners={layer.borderRadiusCorners}
					spacing={layer.spacing}
				/>
			)
		case 'substack':
			return (
				<SubstackLayerRenderer
					layerId={layerId}
					direction={layer.direction}
					alignItems={layer.alignItems}
					justifyContent={layer.justifyContent}
					childSizing={layer.childSizing}
					widthSizing={layer.widthSizing}
					gap={layer.gap}
					fontFamilyMode={layer.fontFamilyMode}
					fontFamilyProvider={layer.fontFamilyProvider}
					fontFamilyGoogleFamily={layer.fontFamilyGoogleFamily}
					fontFamilyCustomUrl={layer.fontFamilyCustomUrl}
					fontFamilyCustomName={layer.fontFamilyCustomName}
					spacing={layer.spacing}
					background={layer.background}
					borderRadius={layer.borderRadius}
					borderRadiusCorners={layer.borderRadiusCorners}
				>
					{children}
				</SubstackLayerRenderer>
			)
		case 'backdrop':
			return <BackdropLayerRenderer layerId={layerId} background={layer.background} mask={layer.mask} />
		default:
			return null
	}
}

export const LayerRenderer = memo(function LayerRenderer() {
	const store = useCanvasStore()
	const layerTree = useAtomValue(layerTreeAtom, { store })

	return <>{[...layerTree].reverse().map(node => <LayerNodeRenderer key={node.id} node={node} />)}</>
})
